// Win Tool (launcher) campaign reads, over its HTTP API — mini-apps/CLAUDE.md
// lane 2 ("Mini-app → mini-app data: HTTP API, never imports"). These are the
// same thin react-query hooks Win Tool uses internally; Playbooks carries its
// own copy because sibling imports are banned. Fetchers live in
// `launcher-api-client.ts` (the test mock seam, itself a thin delegate to the
// shared parsed fetchers). The campaign query keys are NOT retyped here — they
// come from `_shared/launcher-campaign-schema.ts`, which owns the key, the
// schema and the fetcher together so four apps cannot cache four shapes under
// one entry (QA round 8, U2).
import type { QueryClient } from '@tanstack/react-query';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../_shared/hooks';
import type { Campaign, CampaignMember } from '../_shared/launcher-campaign.types';
import { LAUNCHER_SYNC_STATUS_QUERY_KEY, launcherCampaignMembersQueryKey } from '../_shared/launcher-campaign-member-schema';
import { LAUNCHER_CAMPAIGNS_QUERY_KEY, launcherCampaignQueryKey } from '../_shared/launcher-campaign-schema';
import {
  fetchCampaigns,
  fetchCampaign,
  fetchCampaignMembers,
  fetchSyncStatus,
  triggerCampaignSync,
  triggerCampaignMemberSync,
  type LauncherSyncStatus,
  type LauncherMemberSyncResult,
} from './launcher-api-client';
import { completeMemberSync, failMemberSync, memberSyncFailureDetail } from './member-sync-store';
import { matchedMembersPrefixKey } from './query-keys';

const TYPES_KEY = ['launcher', 'campaign-type-config'] as const;
const SYNC_STATUS_KEY = LAUNCHER_SYNC_STATUS_QUERY_KEY;

export function useLauncherCampaigns() {
  const { apiFetch } = useApi();
  const query = useQuery<Campaign[]>({
    queryKey: LAUNCHER_CAMPAIGNS_QUERY_KEY,
    queryFn: () => fetchCampaigns(apiFetch),
    staleTime: Infinity,
  });
  return {
    campaigns: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  } as const;
}

export function useLauncherCampaign(id: string) {
  const { apiFetch } = useApi();
  const query = useQuery<Campaign>({
    queryKey: launcherCampaignQueryKey(id),
    queryFn: () => fetchCampaign(apiFetch, id),
    enabled: !!id,
  });
  return {
    campaign: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  } as const;
}

export function useLauncherMembers(id: string) {
  const { apiFetch } = useApi();
  const query = useQuery<CampaignMember[]>({
    queryKey: launcherCampaignMembersQueryKey(id),
    queryFn: () => fetchCampaignMembers(apiFetch, id),
    enabled: !!id,
    staleTime: 0,
  });
  return {
    members: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  } as const;
}

export function useSyncStatus() {
  const { apiFetch } = useApi();
  const query = useQuery<LauncherSyncStatus>({
    queryKey: SYNC_STATUS_KEY,
    queryFn: () => fetchSyncStatus(apiFetch),
    staleTime: 0,
    refetchInterval: 60_000,
  });
  return {
    syncStatus: query.data ?? null,
    isLoading: query.isLoading,
  } as const;
}

export function useTriggerCampaignSync() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => triggerCampaignSync(apiFetch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TYPES_KEY });
      void queryClient.invalidateQueries({ queryKey: SYNC_STATUS_KEY });
      // The sync rewrites the campaign list; it is cached indefinitely, so refetch it here.
      void queryClient.invalidateQueries({ queryKey: ['launcher', 'campaigns'] });
    },
  });
}

/**
 * Cache reconciliation after a live member sync settles (success OR failure —
 * on failure the mirror is whatever it is, and a previous visit's cached rows
 * must still not be the first thing rendered).
 *
 * `removeQueries`, not `invalidateQueries`, for the two reads that are
 * INACTIVE while `PlayFlowShell` keeps the steps unmounted: invalidating an
 * inactive query only marks it stale, so the remounting `useLauncherMembers`
 * would render the pre-sync rows while refetching in the background — the
 * exact stale flash the gate exists to prevent. Removing forces a cold fetch.
 * The campaign detail/list carry `member_count`/`members_last_synced_at` and
 * stay mounted in the shell's own band, so a background refresh is right for
 * them.
 */
export function settleMemberSyncCaches(queryClient: QueryClient, campaignSfdcId: string): void {
  queryClient.removeQueries({ queryKey: launcherCampaignMembersQueryKey(campaignSfdcId), exact: true });
  queryClient.removeQueries({ queryKey: matchedMembersPrefixKey() });
  void queryClient.invalidateQueries({ queryKey: ['launcher', 'campaigns', campaignSfdcId], exact: true });
  void queryClient.invalidateQueries({ queryKey: ['launcher', 'campaigns'], exact: true });
}

/**
 * Live, per-campaign member resync (plans/2026-09-17-playbooks-live-member-sync-plan.md).
 * Same shape as `useTriggerCampaignSync` above. Store + cache writes live in
 * `onSettled` (options-level, so they run even if the shell that fired the
 * mutation has since unmounted) and in THIS order: caches first, then the
 * store — the store flip is what remounts the steps, and they must remount
 * onto a clean cache. No toasts: success is silent by design (one per play
 * open would be noise) and the failure surface is the shell's own band.
 */
export function useTriggerCampaignMemberSync() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation<LauncherMemberSyncResult, unknown, string>({
    mutationFn: (campaignSfdcId) => triggerCampaignMemberSync(apiFetch, campaignSfdcId),
    onSettled: (data, error, campaignSfdcId) => {
      settleMemberSyncCaches(queryClient, campaignSfdcId);
      if (error) {
        failMemberSync(campaignSfdcId, memberSyncFailureDetail(error));
      } else if (data) {
        completeMemberSync(campaignSfdcId, { memberCount: data.member_count, syncedAt: data.synced_at });
      }
    },
  });
}
